import { useEffect, useLayoutEffect, useState } from 'react';
/**
 * Everything in a computed style that changes how wide a glyph is drawn: the font itself and
 * its features (MCL's cells may set tabular figures, a ligature or kerning setting changes a
 * name's width too), the spacing and the case transform. A name measured without one of these
 * comes out narrower than it is drawn and is then cut off with an ellipsis.
 */
export var PROBE_TEXT_PROPERTIES = [
    'font-family',
    'font-size',
    'font-style',
    'font-weight',
    'font-stretch',
    'font-variant',
    'font-variant-numeric',
    'font-variant-ligatures',
    'font-variant-caps',
    'font-variant-east-asian',
    'font-feature-settings',
    'font-variation-settings',
    'font-kerning',
    'font-optical-sizing',
    'font-synthesis',
    'font-size-adjust',
    'letter-spacing',
    'word-spacing',
    'text-transform',
    'text-rendering',
];
/**
 * Where the names are set: a type name already on screen (the compact label's last child), or,
 * while there is none, a cell, or the list itself.
 */
var TYPE_NAME_ON_SCREEN = '[role=gridcell] [data-testid^=authority-type-label-]:not([role=img]) > :last-child';
/** DB-295 — an authority id already on screen (AuthoritiesList marks its id span). */
export var ID_ON_SCREEN = '[role=gridcell] [data-authority-id]';
/**
 * CZNA-1529 — the width of the widest of `texts` as the browser sets a type name in the list:
 * in every glyph-width property (PROBE_TEXT_PROPERTIES) of a name already on screen, else of a
 * cell (or `container`) at `fontWeight`. Undefined where nothing is laid out (jsdom).
 * DB-295: `onScreen` names what else is measured the same way — `ID_ON_SCREEN` for the ids,
 * with no `fontWeight` (an id is in the cell's own).
 *
 * The probe sits inside `container`, so it inherits what a cell would, in a box of no size that
 * clips it; it is taken out again before the browser paints, and never touches the list's size.
 */
export var widestTextWidth = function (container, texts, fontWeight, onScreen) {
    var _a;
    if (onScreen === void 0) { onScreen = TYPE_NAME_ON_SCREEN; }
    if (texts.length === 0)
        return undefined;
    var name = container.querySelector(onScreen);
    var style = getComputedStyle((_a = name !== null && name !== void 0 ? name : container.querySelector('[role=gridcell]')) !== null && _a !== void 0 ? _a : container);
    var box = document.createElement('div');
    box.setAttribute('aria-hidden', 'true');
    Object.assign(box.style, {
        position: 'absolute', top: '0', left: '0', width: '0', height: '0', overflow: 'hidden', visibility: 'hidden',
    });
    var probe = document.createElement('span');
    PROBE_TEXT_PROPERTIES.forEach(function (property) {
        var value = style.getPropertyValue(property);
        if (value)
            probe.style.setProperty(property, value);
    });
    // A name on screen is already in the label's weight; a cell is not. Without a weight (an
    // id, DB-295) the cell's own is the text's.
    if (!name && fontWeight !== undefined)
        probe.style.setProperty('font-weight', String(fontWeight));
    probe.style.setProperty('position', 'absolute');
    probe.style.setProperty('white-space', 'nowrap');
    box.appendChild(probe);
    container.appendChild(box);
    try {
        var widest = texts.reduce(function (max, text) {
            probe.textContent = text;
            return Math.max(max, probe.getBoundingClientRect().width);
        }, 0);
        return widest > 0 ? widest : undefined;
    }
    finally {
        box.remove();
    }
};
/**
 * `widestTextWidth` of the node `container` returns, kept current: measured again whenever
 * `measureKey` changes (the list's width, its rows) or the texts change, and once a web font
 * finishes loading — measured in the fallback font, a name would come out narrower than it
 * is drawn.
 */
export var useWidestTextWidth = function (container, texts, fontWeight, measureKey, onScreen) {
    var _a = useState(0), fontsLoaded = _a[0], setFontsLoaded = _a[1];
    useEffect(function () {
        var _a, _b;
        var fonts = typeof document !== 'undefined' ? document.fonts : undefined;
        if (!fonts)
            return undefined;
        var live = true;
        var loaded = function () { if (live)
            setFontsLoaded(function (n) { return n + 1; }); };
        (_a = fonts.ready) === null || _a === void 0 ? void 0 : _a.then(loaded, function () { });
        (_b = fonts.addEventListener) === null || _b === void 0 ? void 0 : _b.call(fonts, 'loadingdone', loaded);
        return function () {
            var _a;
            live = false;
            (_a = fonts.removeEventListener) === null || _a === void 0 ? void 0 : _a.call(fonts, 'loadingdone', loaded);
        };
    }, []);
    var _b = useState(), width = _b[0], setWidth = _b[1];
    var textsKey = texts.join('\n');
    useLayoutEffect(function () {
        var node = container();
        setWidth(node ? widestTextWidth(node, texts, fontWeight, onScreen) : undefined);
        // `texts` is compared by its content (textsKey), not by identity.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [container, textsKey, fontWeight, measureKey, fontsLoaded, onScreen]);
    return width;
};
export default useWidestTextWidth;
